export const MULTI_SELECT_ELEMENT = 'multi-select';

export const MULTI_SELECT_SEPARATOR = ', ';

export type MultiSelectOption = { label: string; value?: string | null };

export type MultiSelectAnswer = {
  value: string;
  values: string[];
  meta: { selectedOptionLabel: string; selectedOptionLabels: string[] };
};

/** An option without a value is named by its label, as a single Dropdown select and the browser do. */
export function multiSelectOptionValue(option: MultiSelectOption): string {
  return option.value || option.label;
}

function toValueList(input: unknown): string[] {
  if (Array.isArray(input)) return input.filter((item): item is string => typeof item === 'string');
  if (typeof input === 'string') return [input];
  return [];
}

/**
 * `value` and `values` hold the option values, as a single Dropdown select's `value` does;
 * the labels go in `meta` for emails and the dashboard. Both follow the options' order, not
 * the order ticked, and anything that is not an option's value is dropped.
 */
export function buildMultiSelectAnswer({
  values,
  options,
}: {
  values: unknown;
  options: MultiSelectOption[] | null | undefined;
}): MultiSelectAnswer {
  const chosen = new Set(toValueList(values));
  const selected = (options ?? []).filter(option => chosen.has(multiSelectOptionValue(option)));
  const optionValues = selected.map(multiSelectOptionValue);
  const labels = selected.map(option => option.label);

  return {
    value: optionValues.join(MULTI_SELECT_SEPARATOR),
    values: optionValues,
    meta: { selectedOptionLabel: labels.join(MULTI_SELECT_SEPARATOR), selectedOptionLabels: labels },
  };
}

/**
 * Recovers the chosen option values from a stored `value` when no `values` list is
 * available (abandoned-checkout drafts, URL prefill). A value that itself contains the
 * separator is matched before the string is split, so it survives.
 */
export function splitMultiSelectValue(
  value: unknown,
  options: MultiSelectOption[] | null | undefined,
): string[] {
  if (typeof value !== 'string' || value.trim() === '') return [];

  const optionValues = (options ?? []).map(multiSelectOptionValue);
  const found = new Set<string>();
  let rest = value;

  for (const optionValue of [...optionValues].sort((a, b) => b.length - a.length)) {
    if (!optionValue.includes(MULTI_SELECT_SEPARATOR.trim())) continue;
    if (rest.includes(optionValue)) {
      found.add(optionValue);
      rest = rest.replace(optionValue, '');
    }
  }

  for (const part of rest.split(MULTI_SELECT_SEPARATOR.trim())) {
    const trimmed = part.trim();
    if (trimmed && optionValues.includes(trimmed)) found.add(trimmed);
  }

  return optionValues.filter(optionValue => found.has(optionValue));
}
